//Type imports
import { User } from "../admin/UserTypes";
import { MedicalHistory } from "./MedicalHistoryTypes";
import { Medication } from "./MedicationTypes";
import { Test } from "./TestTypes";
import { PatientNote } from "./PatientNotes";
import { AssignedForm } from "../../api/formApi";
import { Appointment, CompletedAppointment } from "../appointments/AppointmentTypes";
import { SentNotification } from "../../types/communications/NotificationTypes";
import { FileMetadata, ReportFile } from "../file/FileTypes";
import { PatientResourceLink } from "../file/ResourceLinkTypes";
import { PatientTreatmentPlan } from "../healthcare/TreatmentPlanTypes";

// CU-86d438bbx: the explicit opt-in that widens POST /practitioner/getPatient to customers.
// Omitting it keeps the legacy patient-only response the clinical pickers rely on.
export const PATIENT_LIST_SCOPE_WITH_CUSTOMERS = "patient_or_customer";

export const CUSTOMER_USER_TYPE = "customer";

export const PATIENT_USER_TYPE = "patient";

type IdentityTyped = { user_type?: string | null } | null | undefined;

// CU-14ygtkje5r2: clinical actions need a persisted patient; a customer gets the non-clinical record view instead.
export const isPatientIdentity = (record: IdentityTyped): boolean => record?.user_type === PATIENT_USER_TYPE;

export const isCustomerIdentity = (record: IdentityTyped): boolean => record?.user_type === CUSTOMER_USER_TYPE;

export interface Patient extends User {
	medicalHistory?: MedicalHistory[];
	completedAppointments?: CompletedAppointment[];
	notifications?: SentNotification[];
	files?: FileMetadata[];
	resourceLinks?: PatientResourceLink[];
	reports?: ReportFile[];
	treatmentPlans?: PatientTreatmentPlan[];
	patient_info?: PatientInfo;
	medical_history?: MedicalHistory[];
	medications?: Medication[];
	forms?: AssignedForm[];
	tests?: Test[];
	is_anonymous: boolean;
	appointments?: Appointment[];
	has_potential_ihi_demographic_conflict?: boolean;
	ihi_demographic_conflict_detected_at?: string | null;
}

export const defaultPatient: Patient = {
	id: "",
	uuid: "",
	username: "",
	email: "",
	password: "",
	password_confirmation: "",
	first_name: "",
	middle_name: "",
	last_name: "",
	has_one_name_only: false,
	phone: "",
	address: "",
	city: "",
	state: "",
	country: "",
	postal_code: "",
	profile_picture: "",
	date_of_birth: null,
	gender: "",
	sex: "",
	preferences: "",
	is_active: false,
	created_at: new Date(),
	updated_at: new Date(),
	panel_key: "",
	reagents: "",
	patient_info: undefined,
	is_anonymous: false,
	height: "",
	weight: "",
	roles: [],
};

export interface PatientInfo {
	medicare_number?: number;
	medicare_individual_ref_number?: number;
	medicare_valid_to?: Date;
	dva_file_number?: string | null;
	ihi_number?: number;
	ihi_status?: string;
	ihi_record_status?: string;
	ihi_source?: string;
	notes?: PatientNote[] | string;

	// Extended demographic fields (MD importer overhaul T8/M7)
	pronouns?: string;
	is_transgender?: boolean;
	medicare_gender_code?: string;
	deceased_date?: string | Date | null;
	country_of_birth?: string;
	atsi?: boolean;
	interpreter_required?: boolean;
	occupation?: string;
	ethnicities?: PatientEthnicity[];
	additional_emails?: PatientAdditionalEmail[];

	clinical_warnings?: string | null;
	blood_group?: string | null;
	no_known_allergies?: boolean | null;

	// Cliniko integration fields
	cliniko_id?: string;
	cliniko_data?: Record<string, any>;
	cliniko_synced_at?: string;
}

export interface PatientEthnicity {
	id?: string;
	label: string;
}

export interface PatientAdditionalEmail {
	type?: string;
	email: string;
}